import { z } from "zod";

export const policyStageSchema = z.enum([
  "proposal",
  "committee",
  "passed",
  "promulgated",
  "notified",
  "effective",
  "repealed",
  "source_capability"
]);

export const confidenceSchema = z.enum(["official_confirmed", "reviewed_provisional", "review_required"]);
export const impactAxisSchema = z.enum(["money", "right", "duty", "eligibility", "timing", "action"]);

export const profileSchema = z.object({
  employment: z.enum(["employee", "self_employed", "student", "retired", "caregiver", "other", "unknown"]).default("unknown"),
  monthlyPaidHours: z.number().min(0).max(744).nullable().default(null),
  monthlyIncomeKrw: z.number().min(0).max(1_000_000_000).nullable().default(null),
  workplacePension: z.boolean().nullable().default(null),
  pensionCovered: z.boolean().nullable().default(null),
  childrenCount: z.number().int().min(0).max(20).nullable().default(null),
  militaryServiceCompleted: z.boolean().nullable().default(null),
  disabledChildCare: z.boolean().nullable().default(null),
  region: z.string().max(40).nullable().default(null)
});

export type Profile = z.infer<typeof profileSchema>;

export const sourceSchema = z.object({
  id: z.string().min(3),
  title: z.string().min(2),
  publisher: z.string().min(2),
  url: z.string().url(),
  kind: z.enum(["official_api", "official_page", "official_rss", "official_handoff"]),
  checkedAt: z.string().date(),
  rightsNote: z.string().min(4)
});

export const policySchema = z.object({
  id: z.string().regex(/^POL-/),
  slug: z.string().regex(/^[a-z0-9-]+$/),
  family: z.enum(["work_income", "pension_care", "health_care", "civic_law", "family_benefit"]),
  title: z.string().min(4),
  shortTitle: z.string().min(2),
  summary: z.string().min(12),
  jurisdiction: z.string().min(2),
  stage: policyStageSchema,
  confidence: confidenceSchema,
  effectiveAt: z.string().date().nullable(),
  publishedAt: z.string().date(),
  reviewedAt: z.string().date(),
  staleAfterHours: z.number().int().positive(),
  reviewState: z.enum(["verified", "review_required", "hidden_without_current_event"]),
  impactAxes: z.array(impactAxisSchema).min(1),
  audience: z.array(z.string().min(2)).min(1),
  calculatorId: z.enum(["minimum_wage_2027", "pension_2026", "pension_2027", "qualitative", "none"]),
  actionLabel: z.string().min(2).nullable(),
  actionUrl: z.string().url().nullable(),
  sources: z.array(sourceSchema).min(1),
  issueBalance: z.object({
    established: z.array(z.string().min(4)).min(1),
    considerations: z.array(z.string().min(4)).default([])
  }),
  visible: z.boolean().default(true)
});

export type Policy = z.infer<typeof policySchema>;

export const impactLineSchema = z.object({
  axis: impactAxisSchema,
  label: z.string().min(2),
  before: z.string().min(1),
  after: z.string().min(1),
  delta: z.string().min(1),
  basis: z.string().min(4),
  numeric: z.boolean()
});

export const impactResultSchema = z.object({
  policyId: z.string(),
  relevance: z.enum(["applies", "likely", "unknown", "not_applies"]),
  relevanceReason: z.string().min(4),
  headline: z.string().min(4),
  lines: z.array(impactLineSchema).min(1),
  assumptions: z.array(z.string()),
  needsConfirmation: z.array(z.string()),
  sourceIds: z.array(z.string()).min(1),
  calculatedAt: z.string().datetime()
});

export type ImpactResult = z.infer<typeof impactResultSchema>;

export const ingestionDraftSchema = z.object({
  sourceId: z.string().min(3),
  externalId: z.string().min(2),
  title: z.string().min(2),
  canonicalUrl: z.string().url(),
  publishedAt: z.string().datetime(),
  discoveredText: z.string().max(5_000),
  stageCandidate: policyStageSchema,
  numericClaims: z.array(z.object({
    value: z.number(),
    unit: z.string().min(1),
    sourceExcerpt: z.string().min(4)
  })).default([]),
  reviewState: z.literal("review_required")
});

export type IngestionDraft = z.infer<typeof ingestionDraftSchema>;

export const policyTopicIdSchema = z.enum([
  "work", "tax", "housing", "family", "education", "health", "pension", "welfare",
  "business", "transport", "safety", "consumer_finance", "environment", "digital_rights",
  "immigration", "military_veterans", "local_life", "civic_law"
]);

export type PolicyTopicId = z.infer<typeof policyTopicIdSchema>;

export const policyTopicSchema = z.object({
  id: policyTopicIdSchema,
  label: z.string().min(2),
  question: z.string().min(8),
  description: z.string().min(12),
  keywords: z.array(z.string().min(1)).min(3),
  officialUrl: z.string().url(),
  officialPublisher: z.string().min(2)
});

export type PolicyTopic = z.infer<typeof policyTopicSchema>;

export const policyGuideSchema = z.object({
  id: z.string().regex(/^GUIDE-/),
  topicId: policyTopicIdSchema,
  title: z.string().min(4),
  questionExamples: z.array(z.string().min(6)).min(2),
  keywords: z.array(z.string().min(1)).min(3),
  answer: z.string().min(20),
  checks: z.array(z.string().min(3)).min(1),
  actionLabel: z.string().min(2),
  actionUrl: z.string().url(),
  publisher: z.string().min(2),
  matchedPolicyIds: z.array(z.string()).default([]),
  reviewedAt: z.string().date()
});

export type PolicyGuide = z.infer<typeof policyGuideSchema>;

export const answerCitationSchema = z.object({
  title: z.string().min(2),
  publisher: z.string().min(2),
  url: z.string().url(),
  checkedAt: z.string().date()
});

export const answerSheetSchema = z.object({
  query: z.string().min(2).max(500),
  outcome: z.enum(["answer", "clarify", "official_handoff"]),
  title: z.string().min(4),
  summary: z.string().min(8),
  topicIds: z.array(policyTopicIdSchema).min(1),
  matchedPolicyIds: z.array(z.string()),
  confidence: z.enum(["official_confirmed", "guide_match", "needs_context"]),
  facts: z.array(z.string().min(4)).min(1),
  unknowns: z.array(z.string()),
  nextActions: z.array(z.object({label: z.string().min(2), url: z.string().min(1).nullable()})).min(1),
  citations: z.array(answerCitationSchema).min(1),
  resolvedAt: z.string().datetime()
});

export type AnswerSheet = z.infer<typeof answerSheetSchema>;

export const policyUpdateSchema = z.object({
  id: z.string().regex(/^UPDATE-/),
  policyId: z.string(),
  kind: z.enum(["announced", "promulgated", "notified", "effective", "application_open", "deadline", "corrected"]),
  title: z.string().min(4),
  summary: z.string().min(8),
  happenedAt: z.string().date(),
  effectiveAt: z.string().date().nullable(),
  reviewedAt: z.string().date(),
  relevanceHint: z.string().min(4),
  changedFields: z.array(z.string().min(2)).min(1)
});

export type PolicyUpdate = z.infer<typeof policyUpdateSchema>;

export const officialUpdateSignalSchema = z.object({
  id: z.string().regex(/^SIGNAL-/),
  sourceId: z.string().min(3),
  publisher: z.string().min(2),
  department: z.string().min(2).nullable(),
  title: z.string().min(4),
  summary: z.string().min(4),
  sourceUrl: z.string().url(),
  publishedAt: z.string().datetime(),
  collectedAt: z.string().datetime(),
  topicIds: z.array(policyTopicIdSchema),
  summaryKind: z.enum(["source_title", "source_excerpt"]),
  reviewState: z.literal("official_source_unreviewed"),
  legalImpact: z.literal("not_assessed"),
  rightsUrl: z.string().url()
});

export type OfficialUpdateSignal = z.infer<typeof officialUpdateSignalSchema>;

export const sourceHealthItemSchema = z.object({
  id: z.string(),
  kind: z.string(),
  enabled: z.boolean(),
  state: z.enum(["prepared", "waiting_first_run", "healthy", "stale", "failed", "running"]),
  lastRunAt: z.string().datetime().nullable(),
  itemCount: z.number().int().nonnegative(),
  errorCode: z.string().nullable(),
  rightsReviewedAt: z.string().date(),
  robotsReviewedAt: z.string().date().nullable()
});

export const sourceHealthSchema = z.object({
  state: z.enum(["prepared_not_connected", "connected", "partially_connected", "degraded", "health_unavailable"]),
  message: z.string(),
  checkedAt: z.string().datetime(),
  sources: z.array(sourceHealthItemSchema)
});

export type SourceHealth = z.infer<typeof sourceHealthSchema>;

export const actionWindowSchema = z.object({
  id: z.string(),
  policyId: z.string(),
  kind: z.enum(["apply", "comment", "renew", "prepare"]),
  label: z.string().min(2),
  officialUrl: z.string().url(),
  opensAt: z.string().datetime().nullable(),
  closesAt: z.string().datetime().nullable(),
  reviewedAt: z.string().date()
});

export type ActionWindow = z.infer<typeof actionWindowSchema>;

export const analyticsEventSchema = z.discriminatedUnion("name", [
  z.object({name: z.literal("first_value_started"), policyId: z.string()}),
  z.object({name: z.literal("impact_completed"), policyId: z.string(), relevance: z.string()}),
  z.object({name: z.literal("watchlist_diff_opened"), policyId: z.string()}),
  z.object({name: z.literal("official_action_opened"), policyId: z.string()})
]);

export type AnalyticsEvent = z.infer<typeof analyticsEventSchema>;
